import React, { useState, useLayoutEffect, useRef } from 'react';
import AccessEndedGate from './components/Common/AccessEndedGate';   // CEV-S3
import {
  createBrowserRouter, createRoutesFromElements, RouterProvider,
  Route, Navigate, useParams, Outlet,
} from 'react-router-dom';
import {
  Typography,
  Box,
  Menu,
  MenuItem,
  Avatar,
  IconButton,
  Divider,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  FormControl,
  Select,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import { useMsal } from "@azure/msal-react";
// import Dashboard from './components/Dashboard';
import NewDashboard from './components/NewDashboard';
import EmployeeDashboard from './components/EmployeeDashboard';
import HRDashboard from './components/Dashboard/HRDashboard';
import { usePermissions } from './components/PermissionsContext';

// EmployeeList is now embedded in Employee File page, not a standalone route
import EmployeeForm from './components/EmployeeForm';
import ContractList from './components/ContractList';
import ContractForm from './components/ContractForm';
import OfferList from './components/OfferList';
import OfferForm from './components/OfferForm';
import CVUpload from './components/CVUpload';
import PermissionTest from './components/PermissionTest';
import UserProfile from './components/UserProfile';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import { TopBarProvider } from './components/TopBarContext';
import { PermissionsProvider } from './components/PermissionsContext';
import Settings from './components/Settings';
import SeniorityLevelManagement from './components/SeniorityLevelManagement';
import PositionManagement from './components/PositionManagement';
import DepartmentManagement from './components/DepartmentManagement';
import ContractTypeManagement from './components/ContractTypeManagement';
import DocumentTypeManagement from './components/DocumentTypeManagement';
import PositionSkillComposer from './components/PositionSkillComposer';
import NewPersonForm from './components/People/NewPersonForm';
import PersonEditPage from './components/People/PersonEditPage';
import { AuthWrapper } from './components/AuthWrapper';
import BootGate from './components/BootGate';
import { LogoutButton } from './components/LogoutButton';
import LeavePage from './components/Leave/LeavePage';
import MyTimesheetPage from './components/Timesheet/MyTimesheetPage';
import MonthLockManagementPage from './components/Admin/MonthLockManagementPage';
import HoursAuditLogPage from './components/Admin/HoursAuditLogPage';
import AllProjectsReviewPage from './components/Reports/AllProjectsReviewPage';
import ProjectConsumptionPage from './components/Reports/ProjectConsumptionPage';
import ProjectTasksReviewPage from './components/Reports/ProjectTasksReviewPage';
import TeamUtilizationPage from './components/Reports/TeamUtilizationPage';
import TeamCalendarOverviewPage from './components/Reports/TeamCalendarOverviewPage';
import LeaveApprovalsPage from './components/Leave/LeaveApprovalsPage';
import LeaveApproverSettings from './components/Leave/LeaveApproverSettings';
import SickLeaveCalendar from './components/Leave/SickLeaveCalendar';
import { ShortAbsencesPage, ShortAbsenceApprovalsPage, ShortAbsenceReport } from './components/ShortAbsences';

import CompanyDocumentsPage from './components/CompanyDocuments/CompanyDocumentsPage';
import ProjectListPage from './components/Projects/ProjectListPage';
import ProjectImportPage from './components/Projects/ProjectImportPage';
import ProjectDetailPage from './components/Projects/ProjectDetailPage';
// DELIVERY-WI (WI-S1): preview page under /my-projects/:id/delivery.
import DeliveryPage from './components/Projects/Delivery/DeliveryPage';

import { ToastProvider } from './components/ToastProvider';
import CustomToastContainer from './components/Common/CustomToastContainer';
// UX-S1 — in-app replacements for window.confirm + window.alert.
import { ConfirmProvider } from './components/Common/ConfirmDialog';
import { ToastProvider as UxToastProvider } from './components/Common/ToastProvider';
import './styles/toast-custom.css';
import ProtectedRoute from './router/ProtectedRoute';
import Forbidden403 from './components/Forbidden403';
import EmployeeCasePage from './components/EmployeeCase/EmployeeCasePage';
import IdentityAccessAdminPage from './components/Admin/IdentityAccessAdminPage.jsx';
// WI-S6 — the delivery-migration admin page was retired together with
// the one-shot migration service.
import MonthlyLeaveConsumptionReport from './components/Admin/MonthlyLeaveConsumptionReport';
import AnnualLeaveBalanceReport from './components/Admin/AnnualLeaveBalanceReport';
import PaidLeaveReport from './components/Admin/PaidLeaveReport';
import SalaryRegisterPage from './components/Employment/SalaryRegisterPage';   // S3
import SickLeaveReport from './components/Admin/SickLeaveReport';

import NotificationSettings from './components/Notifications/NotificationSettings';
import LeaveBalanceManagement from './components/Admin/LeaveBalanceManagement';
import InitialLeaveEntry from './components/Admin/InitialLeaveEntry';
import ThemeModeProvider from './theme/ThemeModeProvider.jsx';
import CareerEventTypesSettings from './components/CareerEvents/CareerEventTypesSettings';
import ProfileFieldVisibilitySettings from './components/Admin/ProfileFieldVisibilitySettings';
import { EquipmentPage, EquipmentCategoriesAdmin, EquipmentSubcategoriesAdmin, EquipmentAssignmentsAdmin, EquipmentInventoryAdmin } from './components/Equipment';
import EquipmentApprovalsPage from './components/Equipment/EquipmentApprovalsPage';
import EquipmentOrdersPage from './components/Equipment/EquipmentOrdersPage';
import { LicenseVendorsAdmin, LicenseProductsAdmin, LicenseAssignmentsAdmin } from './components/License';
import DocumentViewerRedirect from './components/Documents/DocumentViewerRedirect';

const SkillsLazy = React.lazy(() => import('./components/Skills'));
const SkillCategoriesLazy = React.lazy(() => import('./components/SkillCategoryManagement'));


function GanttRedirect() {
  const { id } = useParams();
  return <Navigate to={`/projects/${id}?view=gantt`} replace />;
}

function HomeEntry() {
  const { loading, role } = usePermissions();
  if (loading) return (
    <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <CircularProgress />
    </Box>
  );
  // HR Dashboard only for HRManager (external consultant)
  if (role === 'HRManager') {
    return <HRDashboard />;
  }
  // Admin dashboard for SystemAdmin and BoardManager
  if (['SystemAdmin', 'BoardManager'].includes(role)) {
    return <NewDashboard />;
  }
  return <EmployeeDashboard />;
}

// Available roles for preview
const PREVIEW_ROLES = [
  { value: '', label: 'My Roles (Exit Preview)' },
  { value: 'Employee', label: 'Employee' },
  { value: 'TeamLead', label: 'Team Lead' },
  { value: 'DepartmentManager', label: 'Department Manager' },
  { value: 'OperationManager', label: 'Operation Manager' },
  { value: 'OfficeAdministrator', label: 'Office Administrator' },
  { value: 'HR', label: 'HR' },
  { value: 'HRManager', label: 'HR Manager' },
  { value: 'BoardManager', label: 'Board Manager' },
];

// User Dropdown Component
const UserDropdown = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { accounts } = useMsal();
  const { realIsSystemAdmin, rolePreview, setRolePreview } = usePermissions();

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleClose();
    window.location.href = '/profile';
  };

  const handleRolePreviewChange = (event) => {
    setRolePreview(event.target.value);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const displayName = accounts[0]?.name || 'User';
  const email = accounts[0]?.username || '';

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Role Preview Indicator */}
        {rolePreview && (
          <Chip
            icon={<PreviewIcon sx={{ fontSize: 16 }} />}
            label={`Viewing as: ${rolePreview}`}
            size="small"
            color="warning"
            onDelete={() => setRolePreview('')}
            sx={{ mr: 1 }}
          />
        )}
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {displayName}
        </Typography>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 1 }}
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: rolePreview ? 'warning.main' : 'primary.main',
              color: rolePreview ? 'warning.contrastText' : 'primary.contrastText',
              fontSize: '0.875rem'
            }}
          >
            {getInitials(displayName)}
          </Avatar>
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 200,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {displayName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {email}
          </Typography>
        </Box>

        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        {/* Role Preview - Only for SystemAdmin */}
        {realIsSystemAdmin && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <PreviewIcon sx={{ fontSize: 14 }} />
                View as Role
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={rolePreview}
                  onChange={handleRolePreviewChange}
                  displayEmpty
                  sx={{ fontSize: '0.875rem' }}
                >
                  {PREVIEW_ROLES.map(r => (
                    <MenuItem key={r.value} value={r.value} sx={{ fontSize: '0.875rem' }}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        <Divider />

        <MenuItem>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <LogoutButton asMenuItem={true} />
        </MenuItem>
      </Menu>
    </>
  );
};

// WI-S4 §1 — the old preview URL redirects to the new home
// /my-projects/:id (Delivery is now the official detail).
const MyProjectDeliveryRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/my-projects/${id}`} replace />;
};

// LF-S5: app chrome — rendered as the layout route under the data router.
function AppLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const toggleSidebar = () => setSidebarCollapsed((c) => !c);

  // AS-S2.1: measure the real top bar height at runtime and publish it as
  // --app-top-bar-height so the shared <Drawer> always sits flush below it.
  // The 56px fallback in index.css is still used until the layout effect runs.
  const topBarWrapRef = useRef(null);
  useLayoutEffect(() => {
    const apply = () => {
      const el = topBarWrapRef.current;
      if (!el) return;
      const h = el.offsetHeight;
      if (h > 0) {
        document.documentElement.style.setProperty('--app-top-bar-height', `${h}px`);
      }
    };
    apply();
    let ro;
    if (topBarWrapRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(apply);
      ro.observe(topBarWrapRef.current);
    }
    return () => { if (ro) ro.disconnect(); };
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%' }}>
      {/* TopBar - Full Width */}
      <Box ref={topBarWrapRef}>
        <TopBar
          userDropdown={<UserDropdown />}
          onMenuToggle={toggleSidebar}
          sidebarCollapsed={sidebarCollapsed}
        />
      </Box>

      {/* Content Area with Sidebar */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: sidebarCollapsed ? '48px 1fr' : '240px 1fr' },
        flex: 1,
        transition: 'grid-template-columns 0.3s ease',
      }}>
        <Sidebar collapsed={sidebarCollapsed} />

        <Box sx={{ backgroundColor: 'background.default', overflow: 'auto', width: '100%' }}>
          <CustomToastContainer />
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

// LF-S5: data router (createBrowserRouter) — enables useBlocker for the
// unsaved-changes navigation guard. All routes nest under the AppLayout route.
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppLayout />}>
                <Route path="/" element={<HomeEntry />} />
                <Route path="/old-dashboard" element={<NewDashboard />} />

                {/* HRManager & SystemAdmin */}
                {/* /employees route removed - use Employee File instead */}
                <Route path="/employees/:id/edit" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<EmployeeForm />} />} />

                <Route path="/contracts" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<ContractList />} />} />
                <Route path="/contracts/new" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<ContractForm />} />} />
                <Route path="/contracts/:id" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<ContractForm />} />} />
                <Route path="/contracts/:id/edit" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<ContractForm />} />} />

                <Route path="/offers" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<OfferList />} />} />
                <Route path="/offers/new" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<OfferForm />} />} />
                <Route path="/offers/:id" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<OfferForm />} />} />
                <Route path="/offers/:id/edit" element={<ProtectedRoute roles={["HRManager","SystemAdmin"]} element={<OfferForm />} />} />

                {/* Employee & HRManager & SystemAdmin */}
                <Route path="/cv-upload" element={<ProtectedRoute roles={["Employee","HRManager","SystemAdmin"]} element={<CVUpload />} />} />

                <Route path="/permissions-test" element={<ProtectedRoute roles={["SystemAdmin"]} element={<PermissionTest />} />} />
                <Route path="/profile" element={<ProtectedRoute roles={["Employee","HRManager","SystemAdmin"]} element={<UserProfile />} />} />

                {/* HR Dossier */}
                <Route path="/admin/employee-case" element={<ProtectedRoute roles={["SystemAdmin","HRManager","BoardManager"]} element={<EmployeeCasePage />} />} />
                {/* Identity & Access (Admin) */}
                <Route path="/admin/identity-access" element={<ProtectedRoute roles={["SystemAdmin"]} element={<IdentityAccessAdminPage />} />} />
                {/* WI-S5 — delivery migration console; SystemAdmin
                    only, deliberately NOT in the left menu. Reached
                    via direct URL. WI-S6 retired this route together
                    with the one-shot migration service. */}
                {/* Leave Consumption Report (Admin) */}
                <Route path="/admin/leave-consumption-report" element={<ProtectedRoute roles={["SystemAdmin","HRManager"]} element={<MonthlyLeaveConsumptionReport />} />} />
                {/* Annual Leave Balance Report (for payroll) */}
                <Route path="/admin/annual-leave-report" element={<ProtectedRoute roles={["SystemAdmin","HRManager","OperationManager","BoardManager"]} element={<AnnualLeaveBalanceReport />} />} />
                {/* Paid Leave Report (PLREP-S1) — xlsx, two sheets */}
                <Route path="/admin/paid-leave-report" element={<ProtectedRoute roles={["SystemAdmin","HRManager","OperationManager","BoardManager"]} element={<PaidLeaveReport />} />} />
                {/* S3 — Salary Register; the API enforces ViewContracts, the page shows a notice without it */}
                <Route path="/reports/salary-register" element={<ProtectedRoute element={<SalaryRegisterPage />} />} />
                {/* Sick Leave Report (Admin) */}
                <Route path="/admin/sick-leave-report" element={<ProtectedRoute roles={["SystemAdmin","HRManager","BoardManager"]} element={<SickLeaveReport />} />} />
                {/* Short Absence Report (Admin) */}
                <Route path="/admin/short-absence-report" element={<ProtectedRoute roles={["SystemAdmin","HRManager","BoardManager"]} element={<ShortAbsenceReport />} />} />

                {/* People (Admin only for now) */}
                <Route path="/people/new" element={<ProtectedRoute roles={["SystemAdmin"]} element={<NewPersonForm />} />} />
                <Route path="/people/:id/edit" element={<ProtectedRoute roles={["SystemAdmin","HRManager"]} element={<PersonEditPage />} />} />
                {/* Removed PersonPickerDemo route (component not present) */}

                {/* Settings (Admin only) */}
                <Route path="/settings" element={<ProtectedRoute roles={["SystemAdmin"]} element={<Settings />} />} />
                <Route path="/settings/seniority-levels" element={<ProtectedRoute roles={["SystemAdmin"]} element={<SeniorityLevelManagement />} />} />
                <Route path="/settings/positions" element={<ProtectedRoute roles={["SystemAdmin"]} element={<PositionManagement />} />} />
                <Route path="/settings/departments" element={<ProtectedRoute roles={["SystemAdmin"]} element={<DepartmentManagement />} />} />
                <Route path="/settings/contract-types" element={<ProtectedRoute roles={["SystemAdmin"]} element={<ContractTypeManagement />} />} />
                <Route path="/settings/document-types" element={<ProtectedRoute roles={["SystemAdmin","HRManager"]} element={<DocumentTypeManagement />} />} />
                <Route path="/settings/career-event-types" element={<ProtectedRoute roles={["SystemAdmin"]} element={<CareerEventTypesSettings />} />} />
                <Route path="/settings/position-skill-sets" element={<ProtectedRoute roles={["SystemAdmin"]} element={<PositionSkillComposer />} />} />
                    <Route path="/settings/notifications" element={<ProtectedRoute roles={["SystemAdmin"]} element={<NotificationSettings />} />} />
                <Route path="/settings/leave-approvers" element={<ProtectedRoute roles={["SystemAdmin"]} element={<LeaveApproverSettings />} />} />
                <Route path="/settings/leave-balances" element={<ProtectedRoute roles={["SystemAdmin"]} element={<LeaveBalanceManagement />} />} />
                <Route path="/settings/initial-leave-entry" element={<ProtectedRoute roles={["SystemAdmin"]} element={<InitialLeaveEntry />} />} />
                <Route path="/settings/profile-visibility" element={<ProtectedRoute roles={["SystemAdmin"]} element={<ProfileFieldVisibilitySettings />} />} />

                {/* Reports — Timesheet (TR-S1) */}
                <Route path="/timesheet/my" element={<ProtectedRoute roles={["Employee","SystemAdmin"]} element={<MyTimesheetPage />} />} />
                <Route path="/my-projects" element={<ProtectedRoute roles={["Employee","SystemAdmin","Consultant"]} element={<ProjectListPage mode="my" />} />} />
                {/* WI-S4 §1 — Delivery is the official detail of My
                    Projects. The old readOnly grid moves behind a
                    "/plan" sub-route so the consultant can still reach
                    Gantt / commercial dates / predecessors for
                    reference. The temporary preview URL redirects. */}
                <Route path="/my-projects/:id" element={<ProtectedRoute roles={["Employee","SystemAdmin","Consultant"]} element={<DeliveryPage />} />} />
                <Route path="/my-projects/:id/plan" element={<ProtectedRoute roles={["Employee","SystemAdmin","Consultant"]} element={<ProjectDetailPage readOnly />} />} />
                <Route path="/my-projects/:id/delivery" element={<MyProjectDeliveryRedirect />} />

                {/* Reports — Management project reports (TR-S3) */}
                <Route path="/reports/projects" element={<ProtectedRoute roles={["ProjectManager","BoardManager","SystemAdmin"]} element={<AllProjectsReviewPage />} />} />
                <Route path="/reports/project-consumption" element={<ProtectedRoute roles={["ProjectManager","BoardManager","SystemAdmin"]} element={<ProjectConsumptionPage />} />} />
                <Route path="/reports/project-tasks" element={<ProtectedRoute roles={["ProjectManager","BoardManager","SystemAdmin"]} element={<ProjectTasksReviewPage />} />} />

                {/* Reports — Team views (TR-S4) */}
                <Route path="/reports/team-utilization" element={<ProtectedRoute roles={["ProjectManager","BoardManager","SystemAdmin"]} element={<TeamUtilizationPage />} />} />
                <Route path="/reports/team-calendar" element={<ProtectedRoute roles={["ProjectManager","BoardManager","SystemAdmin"]} element={<TeamCalendarOverviewPage />} />} />

                {/* Admin — Month Locks (TR-S2) */}
                <Route path="/admin/month-locks" element={<ProtectedRoute roles={["SystemAdmin"]} element={<MonthLockManagementPage />} />} />

                {/* Admin — Hours Audit Log (HLOG-S1) */}
                <Route path="/admin/hours-audit" element={<ProtectedRoute roles={["SystemAdmin"]} element={<HoursAuditLogPage />} />} />

                {/* Leave */}
                <Route path="/leave" element={<ProtectedRoute roles={["Employee","HRManager","SystemAdmin"]} element={<LeavePage />} />} />
                <Route path="/sick-leave" element={<ProtectedRoute roles={["Employee","HRManager","SystemAdmin"]} element={<SickLeaveCalendar />} />} />
                <Route path="/short-absences" element={<ProtectedRoute roles={["Employee","HRManager","BoardManager","SystemAdmin"]} element={<ShortAbsencesPage />} />} />
                <Route path="/short-absence-approvals" element={<ProtectedRoute roles={["DepartmentManager","BoardManager","SystemAdmin"]} element={<ShortAbsenceApprovalsPage />} />} />
                <Route path="/leave-approvals" element={<ProtectedRoute roles={["HRManager","DepartmentManager","BoardManager","SystemAdmin"]} element={<LeaveApprovalsPage />} />} />

                {/* Equipment */}
                <Route path="/equipment" element={<ProtectedRoute roles={["Employee","HRManager","DepartmentManager","BoardManager","SystemAdmin"]} element={<EquipmentPage />} />} />
                <Route path="/equipment-approvals" element={<ProtectedRoute roles={["HRManager","DepartmentManager","BoardManager","SystemAdmin"]} element={<EquipmentApprovalsPage />} />} />
                <Route path="/equipment-orders" element={<ProtectedRoute roles={["OperationManager","SystemAdmin"]} element={<EquipmentOrdersPage />} />} />
                <Route path="/settings/equipment-categories" element={<ProtectedRoute roles={["SystemAdmin"]} element={<EquipmentCategoriesAdmin />} />} />
                <Route path="/settings/equipment-item-types" element={<ProtectedRoute roles={["SystemAdmin"]} element={<EquipmentSubcategoriesAdmin />} />} />
                <Route path="/admin/equipment-assignments" element={<ProtectedRoute roles={["SystemAdmin","OfficeAdministrator","OperationManager"]} element={<EquipmentAssignmentsAdmin />} />} />
                <Route path="/admin/equipment-inventory" element={<ProtectedRoute roles={["SystemAdmin","OfficeAdministrator","OperationManager"]} element={<EquipmentInventoryAdmin />} />} />

                {/* License Management */}
                <Route path="/settings/license-vendors" element={<ProtectedRoute roles={["SystemAdmin","OfficeAdministrator","OperationManager"]} element={<LicenseVendorsAdmin />} />} />
                <Route path="/settings/license-products" element={<ProtectedRoute roles={["SystemAdmin","OfficeAdministrator","OperationManager"]} element={<LicenseProductsAdmin />} />} />
                <Route path="/admin/license-assignments" element={<ProtectedRoute roles={["SystemAdmin","OfficeAdministrator","OperationManager"]} element={<LicenseAssignmentsAdmin />} />} />

                {/* Recruitment */}

                <Route path="/settings/skills" element={<ProtectedRoute roles={["SystemAdmin"]} element={<React.Suspense fallback={null}><SkillsLazy /></React.Suspense>} />} />
                <Route path="/settings/skill-categories" element={<ProtectedRoute roles={["SystemAdmin"]} element={<React.Suspense fallback={null}><SkillCategoriesLazy /></React.Suspense>} />} />

                {/* Company Documents — visible to all authenticated users */}
                <Route path="/company-documents" element={<ProtectedRoute roles={["Employee","HR","HRManager","OperationManager","BoardManager","DepartmentManager","SystemAdmin"]} element={<CompanyDocumentsPage />} />} />

                {/* Projects */}
                <Route path="/projects" element={<ProtectedRoute roles={["SystemAdmin"]} element={<ProjectListPage />} />} />
                <Route path="/projects/import" element={<ProtectedRoute roles={["SystemAdmin"]} element={<ProjectImportPage />} />} />
                <Route path="/projects/:id" element={<ProtectedRoute roles={["SystemAdmin"]} element={<ProjectDetailPage />} />} />
                <Route path="/projects/:id/gantt" element={<ProtectedRoute roles={["SystemAdmin"]} element={<GanttRedirect />} />} />

                {/* Document Viewer (for email links) */}
                <Route path="/documents/view/:documentId" element={<DocumentViewerRedirect />} />

                {/* Forbidden */}
                <Route path="/403" element={<Forbidden403 />} />
    </Route>
  )
);

function App() {
  return (
    <BootGate>
      <AuthWrapper>
        <>
        <AccessEndedGate />{/* CEV-S3 */}
        <ToastProvider>
        <PermissionsProvider>
        <TopBarProvider>
        <ThemeModeProvider>
        {/* UX-S1 — ConfirmProvider + UxToastProvider make useConfirm() /
            useToast() available to every replaced window.confirm / alert
            call site. They sit nearest to RouterProvider so any route
            page can consume the hooks. */}
        <UxToastProvider>
        <ConfirmProvider>
          <RouterProvider router={router} />
        </ConfirmProvider>
        </UxToastProvider>
        </ThemeModeProvider>
        </TopBarProvider>
        </PermissionsProvider>
        </ToastProvider>
        </>
      </AuthWrapper>
    </BootGate>
  );
}

export default App;









